import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The 2026 Victorian state election date (the last Saturday of November).
 * Single source of truth for both the countdown and the displayed date —
 * change this one value and the whole landing page follows.
 */
export const ELECTION_DATE = '2026-11-28';

/**
 * Countdown and date display are anchored to Victoria's timezone so the day
 * count is correct regardless of the viewer's locale or the SSR server clock.
 */
export const ELECTION_TIMEZONE = 'Australia/Melbourne';

export interface ElectionCountdown {
  /** Whole calendar days from today (Melbourne) to the election, clamped to >= 0. */
  daysLeft: number;
  /** Pluralised label, e.g. `"143 days"` or `"1 day"`. */
  daysLeftLabel: string;
  /** The election date formatted for display, e.g. `"28 November 2026"`. */
  formattedDate: string;
  /** `false` once the election date has arrived or passed. */
  isUpcoming: boolean;
  /**
   * `'upcoming'` before the election date, `'ongoing'` on the election date
   * itself (Melbourne calendar day), `'past'` once it's gone by.
   */
  status: 'upcoming' | 'ongoing' | 'past';
}

/**
 * Derives the launch countdown for the landing page from {@link ELECTION_DATE}.
 *
 * Day counting is done on calendar dates via UTC (not on instants) so the
 * result is exact across Melbourne's AEST→AEDT daylight-saving change, which
 * would otherwise make an instant-based diff off by one.
 *
 * @param referenceDate Overrides "now" — used by tests for deterministic results.
 * @param electionDate Overrides {@link ELECTION_DATE} — used once the real
 *   date is known (e.g. fetched from the API) instead of the fallback constant.
 */
export function useElectionCountdown(
  referenceDate?: string | number | Date,
  electionDate: string = ELECTION_DATE,
): ElectionCountdown {
  const todayInVic = (
    referenceDate === undefined ? dayjs() : dayjs(referenceDate)
  )
    .tz(ELECTION_TIMEZONE)
    .format('YYYY-MM-DD');

  const diff = dayjs.utc(electionDate).diff(dayjs.utc(todayInVic), 'day');

  return buildElectionCountdown(
    Math.max(0, diff),
    diff > 0 ? 'upcoming' : diff === 0 ? 'ongoing' : 'past',
    electionDate,
  );
}

/**
 * Shapes an already-known day count into the display contract.
 *
 * Split out so the countdown can be driven by a `days_left`/`status` pair the
 * API has already worked out from its own clock, without duplicating the label
 * and date formatting. See `useElection` for why that is preferred over
 * counting locally.
 */
export function buildElectionCountdown(
  daysLeft: number,
  status: ElectionCountdown['status'],
  electionDate: string,
): ElectionCountdown {
  return {
    daysLeft,
    daysLeftLabel: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`,
    formattedDate: dayjs.utc(electionDate).format('DD MMMM YYYY'),
    isUpcoming: status === 'upcoming',
    status,
  };
}
